import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { 
  Edit, 
  Lock, 
  Unlock, 
  Users, 
  Star, 
  Calendar,
  MapPin,
  Globe,
  Instagram,
  Facebook,
  MessageCircle,
  UserPlus,
  UserCheck,
  Loader,
  Camera,
  TrendingUp,
  IndianRupee,
  Award,
  Activity,
  Image as ImageIcon,
  Briefcase
} from 'lucide-react';
import ProfileEditor from '../components/ProfileEditor';
import BadgeGrid from '../components/BadgeGrid';
import ActivityFeedItem from '../components/ActivityFeedItem';
import FollowerModal from '../components/FollowerModal';
import { getTimeAgo, getDefaultProfileColors } from '../utils/profileHelpers';

const UserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const {
    fetchUserProfile,
    saveUserProfile,
    togglePrivacy,
    fetchActivities,
    fetchBadges,
    fetchFollowers,
    fetchFollowing,
    followUser,
    unfollowUser,
    isFollowingUser,
    bookings,
    offlineJobs,
    expenses,
    reviews
  } = useData();

  const [profile, setProfile] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [badges, setBadges] = useState([]);
  const [activities, setActivities] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [userReviews, setUserReviews] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('photos'); // Default to photos (Instagram-style)
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFollowerModal, setShowFollowerModal] = useState(false);
  const [followerModalType, setFollowerModalType] = useState('followers');
  const [isFollowing, setIsFollowing] = useState(false);
  const [processingFollow, setProcessingFollow] = useState(false);
  
  const targetUserId = userId || (currentUser?.uid || currentUser?.id);
  const isOwnProfile = !userId || targetUserId === (currentUser?.uid || currentUser?.id);

  // Fetch profile data
  useEffect(() => {
    const loadProfileData = async () => {
      if (!targetUserId) return;

      setLoading(true);
      try {
        // Fetch user info from Firebase users collection first
        const { getDoc, doc } = await import('firebase/firestore');
        const { db } = await import('../config/firebase');
        const userDoc = await getDoc(doc(db, 'users', targetUserId));
        let userData = null;
        if (userDoc.exists()) {
          userData = userDoc.data();
          setUserInfo(userData);
        }

        // Fetch profile
        let profileData = await fetchUserProfile(targetUserId);
        
        // If profile doesn't exist, create it for existing user
        if (!profileData && userData) {
          console.log('Creating profile for existing user:', targetUserId);
          const { createUserProfile } = await import('../services/firestoreService');
          await createUserProfile(targetUserId, {
            alias: userData.displayName || userData.email?.split('@')[0] || 'User',
            bio: '',
            profilePhoto: userData.avatar || userData.photoURL || '',
            coverPhoto: '',
            isPublic: true,
            location: userData.location || '',
            website: '',
            instagram: '',
            facebook: ''
          });
          
          // Fetch again after creation
          profileData = await fetchUserProfile(targetUserId);
        }
        
        // If profile exists but profilePhoto is empty, sync from user avatar
        if (profileData && !profileData.profilePhoto && userData && userData.avatar) {
          console.log('Syncing avatar to profile photo:', userData.avatar);
          profileData.profilePhoto = userData.avatar;
          
          // Also save to database for persistence
          const { updateUserProfile } = await import('../services/firestoreService');
          await updateUserProfile(targetUserId, { profilePhoto: userData.avatar });
        }
        
        console.log('Profile data loaded:', {
          profilePhoto: profileData?.profilePhoto,
          userAvatar: userData?.avatar,
          userPhotoURL: userData?.photoURL
        });
        
        setProfile(profileData);

        // Fetch related data
        const [badgesData, activitiesData, followersData, followingData] = await Promise.all([
          fetchBadges(targetUserId),
          fetchActivities(targetUserId),
          fetchFollowers(targetUserId),
          fetchFollowing(targetUserId)
        ]);

        setBadges(badgesData || []);
        setActivities(activitiesData || []);
        setFollowers(followersData || []);
        setFollowing(followingData || []);

        // Check if current user is following
        if (currentUser && !isOwnProfile) {
          const followingStatus = await isFollowingUser(currentUser.uid || currentUser.id, targetUserId);
          setIsFollowing(followingStatus);
        }

        // Fetch reviews
        const { getReviewsByCaregiver } = await import('../services/firestoreService');
        const reviewsResult = await getReviewsByCaregiver(targetUserId);
        if (reviewsResult.success) {
          setUserReviews(reviewsResult.reviews || []);
        }

        // Fetch services if user is a caregiver
        if (userData && userData.role === 'caregiver') {
          const { getCaregiverServices } = await import('../services/servicesService');
          const servicesResult = await getCaregiverServices(targetUserId);
          if (servicesResult.success) {
            setServices(servicesResult.services || []);
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProfileData();
  }, [targetUserId, currentUser, isOwnProfile]);

  // Calculate stats
  const stats = useMemo(() => {
    const userBookings = bookings.filter(b => 
      b.caregiverId === targetUserId || b.ownerId === targetUserId
    );
    const completedBookings = userBookings.filter(b => b.status === 'completed');
    const averageRating = userReviews.length > 0
      ? (userReviews.reduce((sum, r) => sum + r.rating, 0) / userReviews.length).toFixed(1)
      : 0;

    return {
      totalBookings: userBookings.length,
      completedBookings: completedBookings.length,
      reviewCount: userReviews.length,
      averageRating,
      followerCount: followers.length,
      followingCount: following.length,
      badgeCount: badges.length
    };
  }, [bookings, userReviews, followers, following, badges, targetUserId]);

  // Handle privacy toggle
  const handlePrivacyToggle = async () => {
    if (!isOwnProfile) return;
    
    const newPrivacyState = !profile?.isPublic;
    const result = await togglePrivacy(targetUserId, newPrivacyState);
    
    if (result.success) {
      setProfile(prev => ({ ...prev, isPublic: newPrivacyState }));
    }
  };

  // Handle follow/unfollow
  const handleFollowToggle = async () => {
    if (!currentUser || isOwnProfile || processingFollow) return;

    setProcessingFollow(true);
    try {
      if (isFollowing) {
        await unfollowUser(currentUser.uid || currentUser.id, targetUserId);
        setIsFollowing(false);
      } else {
        await followUser(currentUser.uid || currentUser.id, targetUserId);
        setIsFollowing(true);
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
    } finally {
      setProcessingFollow(false);
    }
  };

  // Handle profile save
  const handleProfileSave = async (updates) => {
    const result = await saveUserProfile(targetUserId, updates);
    if (result.success) {
      const updatedProfile = await fetchUserProfile(targetUserId);
      setProfile(updatedProfile);
    }
  };

  // Default cover colors
  const defaultColors = getDefaultProfileColors(targetUserId);

  // Show default profile immediately while loading (Instagram-like)
  const displayName = profile?.alias || userInfo?.displayName || userInfo?.email?.split('@')[0] || 'User';
  const joinDate = profile?.joinedDate || userInfo?.createdAt || new Date();
  const coverPhoto = profile?.coverPhoto || `linear-gradient(135deg, ${defaultColors.from}, ${defaultColors.to})`;
  const profilePhoto = profile?.profilePhoto || userInfo?.photoURL || userInfo?.avatar;
  
  // Create enhanced profile object with synced avatar for ProfileEditor
  const enhancedProfile = profile ? {
    ...profile,
    profilePhoto: profile.profilePhoto || userInfo?.avatar || userInfo?.photoURL || ''
  } : null;

  return (
    <div className="pf-page-wrap md:pb-12">
      {/* Cover Photo Section - Desktop Only */}
      <div className="hidden md:block relative w-full h-80 overflow-hidden">
        {profile?.coverPhoto ? (
          <img src={profile.coverPhoto} alt="Cover" className="w-full h-full object-cover" />
        ) : (
          <div 
            className="w-full h-full"
            style={{ background: coverPhoto }}
          />
        )}
        
        {/* Privacy Toggle (Own Profile) - Desktop */}
        {isOwnProfile && (
          <button
            onClick={handlePrivacyToggle}
            className="absolute top-4 right-4 p-3 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-all shadow-lg"
          >
            {profile?.isPublic ? (
              <Unlock size={20} className="text-green-600" />
            ) : (
              <Lock size={20} className="text-red-600" />
            )}
          </button>
        )}
      </div>

      {/* Profile Content */}
      <div className="max-w-6xl mx-auto px-0 md:px-4 md:-mt-24 relative z-10">
        {/* Profile Header Card */}
        <div className="pf-organic-card md:rounded-2xl overflow-hidden">
          {/* Mobile: Compact Instagram-style Layout */}
          <div className="md:hidden">
            <div className="flex items-start gap-4 p-4 pb-2">
              {/* Profile Photo - Smaller on Mobile */}
              <div className="flex-shrink-0">
                <div className="relative">
                  {profilePhoto ? (
                    <img
                      src={profilePhoto}
                      alt={displayName}
                      className="w-20 h-20 rounded-full object-cover border-2 border-white shadow-md"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-[var(--pf-accent)] flex items-center justify-center text-white text-2xl font-bold border-2 border-white shadow-md">
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
              </div>

              {/* Stats - Inline on Mobile */}
              <div className="flex-1 grid grid-cols-3 gap-2 text-center pt-2">
                <div>
                  <div className="text-lg font-bold text-gray-900">{stats.totalBookings}</div>
                  <div className="text-xs text-gray-600">Bookings</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-gray-900">{stats.completedBookings}</div>
                  <div className="text-xs text-gray-600">Completed</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-gray-900 flex items-center justify-center gap-0.5">
                    {stats.averageRating}
                    <Star size={14} className="text-yellow-500 fill-yellow-500" />
                  </div>
                  <div className="text-xs text-gray-600">Rating</div>
                </div>
              </div>
            </div>

            {/* Name and Bio - Mobile */}
            <div className="px-4 pb-3">
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-base font-bold text-gray-900">{displayName}</h1>
                {userInfo?.role && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--pf-accent-soft)] text-[var(--pf-accent)] font-medium capitalize">
                    {userInfo.role}
                  </span>
                )}
              </div>
              {profile?.bio ? (
                <p className="text-sm text-gray-700 leading-snug">{profile.bio}</p>
              ) : (
                <p className="text-sm text-gray-400 italic">
                  {isOwnProfile ? 'Add a bio' : 'No bio yet'}
                </p>
              )}
            </div>

            {/* Action Buttons - Mobile */}
            <div className="px-4 pb-3 flex gap-2">
              {isOwnProfile ? (
                <>
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="flex-1 py-1.5 bg-gray-200 text-gray-900 rounded-lg font-semibold text-sm hover:bg-gray-300 transition-all"
                  >
                    Edit Profile
                  </button>
                  <button
                    onClick={handlePrivacyToggle}
                    className="px-3 py-1.5 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 transition-all"
                  >
                    {profile?.isPublic ? (
                      <Unlock size={16} className="text-green-600" />
                    ) : (
                      <Lock size={16} className="text-red-600" />
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleFollowToggle}
                    disabled={processingFollow}
                    className={`flex-1 py-1.5 rounded-lg font-semibold text-sm transition-all ${
                      isFollowing
                        ? 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                        : 'bg-[var(--pf-accent)] text-white hover:opacity-90'
                    } disabled:opacity-50`}
                  >
                    {processingFollow ? (
                      <Loader className="animate-spin mx-auto" size={16} />
                    ) : isFollowing ? (
                      'Following'
                    ) : (
                      'Follow'
                    )}
                  </button>
                        <button className="flex-1 py-1.5 bg-[var(--pf-accent-soft)] text-[var(--pf-accent)] rounded-lg font-semibold text-sm hover:opacity-90 transition-all">
                          Message
                        </button>
                </>
              )}
            </div>
          </div>

          {/* Desktop: Original Layout */}
          <div className="hidden md:block p-8">
            <div className="flex gap-6">
              {/* Profile Photo */}
              <div className="flex-shrink-0">
                <div className="relative">
                  {profilePhoto ? (
                    <img
                      src={profilePhoto}
                      alt={displayName}
                      className="w-40 h-40 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                  ) : (
                    <div className="w-40 h-40 rounded-full bg-[var(--pf-accent)] flex items-center justify-center text-white text-5xl font-bold border-4 border-white shadow-lg">
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  {isOwnProfile && !loading && (
                    <button 
                      onClick={() => setShowEditModal(true)}
                      className="absolute bottom-0 right-0 p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                    >
                      <Camera size={20} className="text-gray-700" />
                    </button>
                  )}
                </div>
              </div>

              {/* Profile Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                      {displayName}
                      {userInfo?.role && (
                        <span className="text-sm px-3 py-1 rounded-full bg-[var(--pf-accent-soft)] text-[var(--pf-accent)] font-medium capitalize">
                          {userInfo.role}
                        </span>
                      )}
                    </h1>
                    {profile?.bio ? (
                      <p className="text-gray-600 mt-2 leading-relaxed">{profile.bio}</p>
                    ) : (
                      <p className="text-gray-400 mt-2 italic">
                        {isOwnProfile ? 'Add a bio to tell others about yourself' : 'No bio yet'}
                      </p>
                    )}
                  </div>

                  {/* Action Buttons - Desktop */}
                  <div className="flex gap-2">
                    {isOwnProfile ? (
                      <button
                        onClick={() => setShowEditModal(true)}
                        className="pf-btn-primary inline-flex items-center gap-2"
                      >
                        <Edit size={18} />
                        Edit Profile
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={handleFollowToggle}
                          disabled={processingFollow}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                            isFollowing
                              ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              : 'pf-btn-primary flex items-center gap-2'
                          } disabled:opacity-50`}
                        >
                          {processingFollow ? (
                            <Loader className="animate-spin" size={18} />
                          ) : isFollowing ? (
                            <>
                              <UserCheck size={18} />
                              Following
                            </>
                          ) : (
                            <>
                              <UserPlus size={18} />
                              Follow
                            </>
                          )}
                        </button>
                        <button className="flex items-center gap-2 px-4 py-2 bg-[var(--pf-accent)] text-white rounded-lg font-medium hover:opacity-90 transition-all">
                          <MessageCircle size={18} />
                          Message
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Meta Info - Desktop */}
                <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
                  {profile?.location && (
                    <div className="flex items-center gap-1">
                      <MapPin size={16} />
                      {profile.location}
                    </div>
                  )}
                  {joinDate && (
                    <div className="flex items-center gap-1">
                      <Calendar size={16} />
                      Joined {getTimeAgo(joinDate)}
                    </div>
                  )}
                  {profile?.website && (
                    <a href={profile.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-blue-600 transition-colors">
                      <Globe size={16} />
                      Website
                    </a>
                  )}
                  {profile?.instagram && (
                    <a href={`https://instagram.com/${profile.instagram}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-pink-600 transition-colors">
                      <Instagram size={16} />
                      {profile.instagram}
                    </a>
                  )}
                  {profile?.facebook && (
                    <a href={profile.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-blue-600 transition-colors">
                      <Facebook size={16} />
                      Facebook
                    </a>
                  )}
                </div>

                {/* Stats Bar - Desktop */}
                <div className="grid grid-cols-4 gap-4 p-4 bg-[var(--pf-canvas)] rounded-xl border border-[var(--pf-border)]">
                  <div className="text-center p-2">
                    <div className="text-2xl font-bold text-gray-900">{stats.totalBookings}</div>
                    <div className="text-sm text-gray-600">Total Bookings</div>
                  </div>
                  <div className="text-center p-2">
                    <div className="text-2xl font-bold text-gray-900">{stats.completedBookings}</div>
                    <div className="text-sm text-gray-600">Completed</div>
                  </div>
                  <div className="text-center p-2">
                    <div className="text-2xl font-bold text-gray-900 flex items-center justify-center gap-1">
                      {stats.averageRating}
                      <Star size={20} className="text-yellow-500 fill-yellow-500" />
                    </div>
                    <div className="text-sm text-gray-600">{stats.reviewCount} Reviews</div>
                  </div>
                  <div className="text-center p-2">
                    <div className="text-2xl font-bold text-gray-900">{stats.badgeCount}</div>
                    <div className="text-sm text-gray-600">Badges Earned</div>
                  </div>
                </div>

                {/* Badges Section - Desktop */}
                {badges.length > 0 && (
                  <div className="mt-4">
                    <BadgeGrid badges={badges} maxDisplay={6} size="small" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="pf-organic-card md:rounded-2xl overflow-hidden md:mt-6">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              {[
                { id: 'about', label: 'About', icon: Users },
                { id: 'photos', label: 'Photos', icon: ImageIcon },
                ...(userInfo?.role === 'caregiver' ? [{ id: 'services', label: 'Services', icon: Briefcase }] : []),
                { id: 'activity', label: 'Activity', icon: Activity },
                { id: 'reviews', label: 'Reviews', icon: Star },
                { id: 'stats', label: 'Stats', icon: TrendingUp }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center justify-center gap-1 md:gap-2 flex-1 md:flex-initial px-3 md:px-6 py-3 md:py-4 font-medium transition-colors border-b-2 whitespace-nowrap text-xs md:text-base ${
                    activeTab === tab.id
                      ? 'text-[var(--pf-accent)] border-[var(--pf-accent)]'
                      : 'text-[var(--pf-ink-muted)] border-transparent hover:text-[var(--pf-ink)]'
                  }`}
                >
                  <tab.icon size={16} className="md:w-[18px] md:h-[18px]" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-3 md:p-6">
            {activeTab === 'about' && (
              <div className="space-y-3 md:space-y-6">
                <div>
                  <h3 className="text-base md:text-xl font-bold text-gray-900 mb-1 md:mb-2">Bio</h3>
                  {profile?.bio ? (
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed">{profile.bio}</p>
                  ) : (
                    <div className="text-center py-4 md:py-8 pf-organic-card">
                      <p className="text-sm md:text-base text-[var(--pf-ink-muted)]">
                        {isOwnProfile ? 'Add a bio to tell others about yourself' : 'No bio added yet'}
                      </p>
                      {isOwnProfile && (
                        <button
                          onClick={() => setShowEditModal(true)}
                          className="mt-2 md:mt-3 pf-btn-primary text-sm md:text-base"
                        >
                          Add Bio
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2 md:mb-4">Stats Overview</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                    <div className="p-2 md:p-4 bg-[var(--pf-accent-soft)] rounded-lg md:rounded-xl">
                      <div className="text-lg md:text-2xl font-bold text-[var(--pf-accent)]">{stats.totalBookings}</div>
                      <div className="text-xs md:text-sm text-gray-600">Total Bookings</div>
                    </div>
                    <div className="p-2 md:p-4 bg-[var(--pf-mint)] rounded-lg md:rounded-xl border border-[var(--pf-mint-deep)]">
                      <div className="text-lg md:text-2xl font-bold text-[var(--pf-accent)]">{stats.completedBookings}</div>
                      <div className="text-xs md:text-sm text-gray-600">Completed</div>
                    </div>
                    <div className="p-2 md:p-4 bg-[var(--pf-accent-soft)] rounded-lg md:rounded-xl">
                      <div className="text-lg md:text-2xl font-bold text-[var(--pf-accent)]">{stats.badgeCount}</div>
                      <div className="text-xs md:text-sm text-gray-600">Badges Earned</div>
                    </div>
                    <div className="p-2 md:p-4 bg-[var(--pf-surface)] rounded-lg md:rounded-xl border border-[var(--pf-border)]">
                      <div className="text-lg md:text-2xl font-bold text-[var(--pf-accent)]">{stats.followingCount}</div>
                      <div className="text-xs md:text-sm text-gray-600">Connections</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'photos' && (
              <div>
                <div className="flex items-center justify-between mb-2 md:mb-4">
                  <h3 className="text-sm md:text-xl font-bold text-gray-900">
                    Photos ({profile?.totalPhotos || 0}/10)
                  </h3>
                  {isOwnProfile && !loading && (
                    <button
                      onClick={() => setShowEditModal(true)}
                      className="pf-btn-primary text-xs md:text-sm"
                    >
                      Add
                    </button>
                  )}
                </div>
                {profile?.photos && profile.photos.length > 0 ? (
                  <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-1 md:gap-4">
                    {profile.photos.map((photo, index) => (
                      <div key={index} className="aspect-square rounded overflow-hidden">
                        <img src={photo} alt={`Gallery ${index + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 md:py-16 pf-organic-card">
                    <ImageIcon size={48} className="mx-auto text-[var(--pf-ink-muted)] mb-3 md:mb-4 md:w-16 md:h-16 opacity-40" />
                    <p className="text-sm md:text-base text-[var(--pf-ink-muted)] mb-3 md:mb-4">
                      {isOwnProfile ? 'Share your favorite moments' : 'No photos uploaded yet'}
                    </p>
                    {isOwnProfile && (
                      <button
                        onClick={() => setShowEditModal(true)}
                        className="pf-btn-primary"
                      >
                        Upload Photos
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="space-y-2 md:space-y-3">
                {activities.length > 0 ? (
                  activities.map((activity) => (
                    <ActivityFeedItem key={activity.id} activity={activity} />
                  ))
                ) : (
                  <div className="text-center py-12 md:py-16 pf-organic-card">
                    <Activity size={48} className="mx-auto text-gray-300 mb-3 md:mb-4 md:w-16 md:h-16" />
                    <p className="text-sm md:text-base text-gray-500">
                      {isOwnProfile ? 'Your activities will appear here' : 'No activities yet'}
                    </p>
                    <p className="text-xs md:text-sm text-gray-400 mt-1 md:mt-2">
                      {isOwnProfile && 'Activities like bookings, reviews, and badges will show up here'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="space-y-2 md:space-y-4">
                {userReviews.length > 0 ? (
                  userReviews.map((review) => (
                    <div key={review.id} className="p-3 md:p-4 border border-gray-200 rounded-lg md:rounded-xl">
                      <div className="flex items-center justify-between mb-1 md:mb-2">
                        <div className="flex items-center gap-0.5 md:gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              size={14}
                              className={`md:w-4 md:h-4 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                        <span className="text-xs md:text-sm text-gray-500">{getTimeAgo(review.createdAt)}</span>
                      </div>
                      <p className="text-sm md:text-base text-gray-700">{review.comment}</p>
                      <p className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2">- {review.ownerName || 'Anonymous'}</p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 md:py-16 pf-organic-card">
                    <Star size={48} className="mx-auto text-gray-300 mb-3 md:mb-4 md:w-16 md:h-16" />
                    <p className="text-sm md:text-base text-gray-500">
                      {isOwnProfile ? 'Your reviews will appear here' : 'No reviews yet'}
                    </p>
                    <p className="text-xs md:text-sm text-gray-400 mt-1 md:mt-2">
                      {userInfo?.role === 'caregiver' && 'Complete bookings to receive reviews'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'services' && (
              <div className="space-y-3 md:space-y-4">
                {services.length > 0 ? (
                  services.map((service) => (
                    <div key={service.id} className="pf-organic-card p-4 hover:border-[var(--pf-accent)] transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900 text-base md:text-lg">{service.name}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              service.available 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {service.available ? 'Available' : 'Unavailable'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-xl md:text-2xl font-bold text-[var(--pf-accent)]">₹{service.rate}</p>
                          <p className="text-xs text-gray-500">{service.rateType}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          <span>Up to {service.maxPets} pets</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 md:py-16 pf-organic-card">
                    <Briefcase size={48} className="mx-auto text-gray-300 mb-3 md:mb-4 md:w-16 md:h-16" />
                    <p className="text-sm md:text-base text-gray-500">
                      {isOwnProfile ? 'Add your first service to get started' : 'No services listed yet'}
                    </p>
                    {isOwnProfile && userInfo?.role === 'caregiver' && (
                      <Link
                        to="/caregiver-services"
                        className="inline-block mt-4 pf-btn-primary"
                      >
                        Add Service
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'stats' && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-900">Performance Overview</h3>
                <p className="text-gray-600">
                  Detailed statistics and analytics are coming soon!
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showEditModal && profile && (
        <ProfileEditor
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          profile={enhancedProfile}
          onSave={handleProfileSave}
        />
      )}

      {showFollowerModal && (
        <FollowerModal
          isOpen={showFollowerModal}
          onClose={() => setShowFollowerModal(false)}
          userId={targetUserId}
          type={followerModalType}
          initialData={followerModalType === 'followers' ? followers : following}
        />
      )}
    </div>
  );
};

export default UserProfile;

